// Resolves which profile a creator's Toolkit works on, plus its preview data.
// Shared by the editor route and the owner-only draft preview route so both
// load the exact same profile, metadata and portfolio payload.

import { organizationBySlugQueryOptions } from "@/lib/queries/organization";
import { profileListQueryOptions } from "@/lib/queries/profile";
import { portfolioDraftQueryOptions } from "@/lib/queries/public-profile";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { z } from "zod";

// `?profile=<slug>` picks which of the org's profiles the Toolkit works on.
// Shared by the editor and preview routes' validateSearch.
// TanStack's default parser JSON-parses search values, so a hand-typed
// `?profile=123` (slugs may be all digits) arrives as a number; stringify it so
// it still matches (or is reported not found) instead of being dropped.
export const toolkitSearchSchema = z.object({
  profile: z
    .union([z.string(), z.number()])
    .transform(String)
    .optional()
    .catch(undefined),
});

// Saving portfolio metadata goes through `organization.profiles.update`, an
// admin procedure (owner or admin). The draft preview is gated on the same
// roles, so nobody can view a draft they couldn't edit.
export function canEditPortfolio(role: string | undefined): boolean {
  return role === "owner" || role === "admin";
}

/**
 * The member's effective org role, as the API computes it: getBySlug's
 * `currentUserRole` comes from getActiveOrgRole, which elevates platform
 * admins/superadmins to "owner". Falls back to the membership row's role.
 */
export function effectiveOrgRole(context: {
  organization: { currentUserRole?: string | null };
  role: string;
}): string {
  return context.organization.currentUserRole ?? context.role;
}

/**
 * The profile a per-profile page works on: the `?profile=<slug>` match, else
 * the earliest-created profile (a creator org normally has one). A slug that
 * matches nothing returns undefined. Shared by Toolkit and Earnings.
 */
export function selectOrgProfile<
  T extends { slug: string; createdAt: string | Date },
>(profiles: T[] | undefined, profileSlug: string | undefined): T | undefined {
  if (!profiles || profiles.length === 0) return undefined;
  if (profileSlug) return profiles.find((p) => p.slug === profileSlug);
  return [...profiles].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  )[0];
}

export function useToolkitProfile(
  orgSlug: string,
  profileSlugParam: string | undefined,
) {
  const { data: org, isPending: orgPending } = useQuery(
    organizationBySlugQueryOptions(orgSlug),
  );
  const organizationId = org?.id ?? "";

  const { data: profiles, isPending: profilesPending } = useQuery({
    ...profileListQueryOptions(organizationId),
    enabled: Boolean(organizationId),
  });

  // Which profile does this creator's toolkit edit? Honour ?profile=<slug>;
  // with no slug, use the earliest-created profile (a creator org normally has
  // one). A requested slug that matches nothing resolves to no profile (and
  // `profileNotFound`) rather than silently showing a different one.
  const target = useMemo(
    () => selectOrgProfile(profiles, profileSlugParam),
    [profiles, profileSlugParam],
  );
  const profileNotFound = Boolean(
    profileSlugParam && profiles && profiles.length > 0 && !target,
  );

  const {
    data: previewData,
    isPending: previewPending,
    isError: previewError,
  } = useQuery({
    ...portfolioDraftQueryOptions(organizationId, target?.id ?? ""),
    enabled: Boolean(organizationId && target?.id),
    // The creator's own editing surface must never trust a cached snapshot
    // (EPD-12133): revalidate on every mount/focus so profile edits — from the
    // profile editor in this tab or another — show without a hard refresh.
    // The cached payload still paints instantly while the refetch runs.
    staleTime: 0,
  });

  return {
    organizationId,
    profiles,
    target,
    // A ?profile=<slug> was requested but no profile in the org has that slug.
    profileNotFound,
    // Org + profile list resolved (target may still be undefined: no profile).
    isPending: orgPending || profilesPending,
    previewData,
    previewPending,
    previewError,
  };
}
